import React, { useState, Fragment, useContext, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import classnames from 'classnames';
import { useHistory } from 'react-router-dom';
import Loading from '../../../components/common/Loading';
import LeftNav from '../../../components/nav/LeftNav';
import styles from './PatientProfilePage.module.scss';
import AuthContext from '../../../context/AuthContext';
import {
  updatePatientProfile,
  uploadPatientProfilePic,
  removePatientProfilePic,
  closePatientAccount,
} from '../../../api/patient';
import useIntl from '../../../hooks/useIntl';
import messages from './messages.js';
import Modal from '../../../components/common/Modal';
import { Formik, Form } from 'formik';
import { requiredValidator, phoneValidator } from '../../../utils/validators';
import { Cell, Grid } from 'react-foundation';
import PrimaryButton from '../../../components/common/buttons/PrimaryButton';
import SecondaryButton from '../../../components/common/buttons/SecondaryButton';
import FieldInput from '../../../components/common/fields/FieldInput/FieldInput';
import Dropzone from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { logout } from '../../../api/auth';

const fieldValidators = ({ first_name, last_name, phone }) => ({
  ...requiredValidator(first_name, 'first_name'),
  ...requiredValidator(last_name, 'last_name'),
  ...phoneValidator(phone, 'phone'),
});
const cropConfig = {
  x: 180,
  y: 180,
  unit: 'px',
  width: 160,
  height: 160,
};
const PatientProfilePage = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { authState, oktaAuth } = useOktaAuth();
  const { isLoading, authUser, userAvtar, dispatch } = useContext(AuthContext);
  const [dataLoading, setLoading] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCloseAccountlModal, setShowCloseAccountlModal] = useState(false);
  const [profile, setProfile] = useState({});
  const isDataLoading = dataLoading || isLoading;
  const [showProfilePicDialog, setShowProfilePicDialog] = useState(false);
  const [image, setImage] = useState(require(`../../../img/doctor.jpg`));
  const [cropObj, setCropObj] = useState(cropConfig);
  const [imageRef, setImageRef] = useState('');
  const [imageBlob, setImageBlob] = useState('');
  const [error, setError] = useState('');

  const fetchPatient = async () => {
    if (authUser && authUser.id) {
      try {
        setProfile(authUser);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPatient();
  }, [authUser]);

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const { patient } = await updatePatientProfile(
        { id: authUser.id, params: values },
        dispatch,
      );
      setProfile(patient);
      setLoading(false);
      setShowEditDialog(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const onCloseAccount = async () => {
    setLoading(true);
    try {
      await closePatientAccount(authUser.id);
      if (authState.isAuthenticated) {
        oktaAuth.signOut({
          postLogoutRedirectUri: window.location.origin,
        });
      }
      logout(dispatch);
      history.push('/');
      setLoading(false);
      setShowCloseAccountlModal(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const onDrop = (files) => {
    if (files.length === 0) {
      setError('File size must be less than 2 MB');
    }
    setShowProfilePicDialog(true);
    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
    };
    if (files.length) {
      reader.readAsDataURL(files[0]);
    }
  };

  const onImageLoaded = (img) => {
    setImageRef(img);
  };

  const onCropComplete = (crop) => {
    makeClientCrop(crop);
  };

  const onCropChange = (crop /*percentCrop*/) => {
    setCropObj(crop); // we could also use percentCrop:
  };

  const makeClientCrop = async (crop) => {
    if (imageRef && crop.width && crop.height) {
      const croppedImageBlob = await getCroppedImg(
        imageRef,
        crop,
        `${Date().now}.jpeg`,
      );
      setImageBlob(croppedImageBlob);
    }
  };

  const getCroppedImg = (image, crop, fileName) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height,
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          console.error('Canvas is empty');
          return;
        }
        blob.name = fileName;
        resolve(blob);
      }, 'image/jpeg');
    });
  };

  const onClickUploadProfilePic = async () => {
    const formData = new FormData();
    formData.append('file', imageBlob, `${Date().now}.jpeg`);
    setLoading(true);
    try {
      const { patient } = await uploadPatientProfilePic(
        { id: authUser.id, params: formData },
        dispatch,
      );
      setProfile(patient);
      setLoading(false);
      setShowProfilePicDialog(false);
      setCropObj(cropConfig);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const onClickRemoveProfilePic = async () => {
    setLoading(true);
    try {
      const { patient } = await removePatientProfilePic(
        { id: authUser.id },
        dispatch,
      );
      setProfile(patient);
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div medium={3} className={classnames(styles.leftNav, 'desktop-only')}>
        <LeftNav />
      </div>
      {isDataLoading ? (
        <Loading />
      ) : (
        <Fragment>
          <div className={styles.content}>
            <div
              medium={3}
              className={classnames(styles.toptnav, 'mobile-only')}
            />
            <h1 data-testid="patient myaccount header">
              {formatMessage(messages.title)}
            </h1>
            <Grid className={styles.profileGrid}>
              <Cell small={12} medium={3} className={styles.profilePic}>
                <Cell small={12}>
                  <img
                    src={userAvtar}
                    data-testid="patient picture"
                    alt="profilePic"
                  />
                </Cell>
                <Cell small={12}>
                  <Dropzone
                    onDrop={onDrop}
                    accept="image/jpeg, image/png"
                    maxSize={2000000}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        <PrimaryButton
                          label={formatMessage(
                            profile.profile_pic
                              ? messages.editProfileBtnLabel
                              : messages.uploadProfileBtnLabel,
                          )}
                          className="font-18"
                          type="button"
                          data-testid="patient edit profilepic"
                        />
                      </div>
                    )}
                  </Dropzone>
                </Cell>
                {profile.profile_pic && (
                  <Cell small={12} className={styles.removeBtnCell}>
                    <SecondaryButton
                      label={formatMessage(messages.removeProfileBtnLabel)}
                      className="font-18"
                      type="button"
                      onClick={() => onClickRemoveProfilePic()}
                      data-testid="patient remove profilepic"
                    />
                  </Cell>
                )}
              </Cell>
              <Cell small={12} medium={9} className={styles.info}>
                <Grid className={styles.infoGrid}>
                  <Cell className={styles.lableCell} small={5} medium={3}>
                    <p data-testid="name_label">
                      {formatMessage(messages.nameLabel)}
                    </p>
                  </Cell>
                  <Cell className={styles.valueCell} small={7} medium={9}>
                    <p data-testid="name_value">
                      {profile.first_name}{' '}
                      {profile.last_name ? profile.last_name : ''}
                    </p>
                  </Cell>
                  <Cell className={styles.lableCell} small={5} medium={3}>
                    <p data-testid="email_label">
                      {formatMessage(messages.emailLabel)}
                    </p>
                  </Cell>
                  <Cell
                    className={[styles.valueCell, styles.email].join(' ')}
                    small={7}
                    medium={9}
                  >
                    <p data-testid="email_value">{profile.email}</p>
                  </Cell>
                  <Cell className={styles.lableCell} small={5} medium={3}>
                    <p data-testid="phone_label">
                      {formatMessage(messages.phoneLabel)}
                    </p>
                  </Cell>
                  <Cell className={styles.valueCell} small={7} medium={9}>
                    <p data-testid="phone_value">{profile.phone}</p>
                  </Cell>
                  <Cell className={styles.lableCell} small={5} medium={3}>
                    <p data-testid="password_label">
                      {formatMessage(messages.passwordLabel)}
                    </p>
                  </Cell>
                  <Cell className={styles.valueCell} small={7} medium={9}>
                    <p data-testid="password_value">************</p>
                  </Cell>
                  <Cell
                    className={classnames(styles.lableCell, styles.fullwidth)}
                    small={5}
                    medium={3}
                  >
                    <p
                      className={styles.link}
                      data-testid="patient edit myaccount"
                      onClick={() => setShowEditDialog(true)}
                    >
                      {formatMessage(messages.editProfileLinkLabel)}
                    </p>
                  </Cell>
                  <Cell className={styles.valueCell} small={7} medium={9}>
                    <></>
                  </Cell>
                  <Cell
                    className={classnames(styles.lableCell, styles.fullwidth)}
                    small={5}
                    medium={3}
                  >
                    <p
                      className={styles.link}
                      data-testid="patient edit myuserDetails"
                      onClick={() => history.push(`/my-account/user-details/`)}
                    >
                      {formatMessage(messages.editPersonalInfoLinkLabel)}
                    </p>
                  </Cell>
                  <Cell className={styles.valueCell} small={7} medium={9}>
                    <></>
                  </Cell>
                  <Cell
                    className={classnames(styles.lableCell, styles.fullwidth)}
                    small={5}
                    medium={3}
                  >
                    <p
                      className={styles.link}
                      onClick={() => setShowCloseAccountlModal(true)}
                      data-testid="patient close myaccount"
                    >
                      {formatMessage(messages.closeAccountLinkLabel)}
                    </p>
                  </Cell>
                  <Cell className={styles.valueCell} small={7} medium={9}>
                    <></>
                  </Cell>
                </Grid>
              </Cell>
            </Grid>
          </div>
        </Fragment>
      )}
      {showEditDialog && (
        <Modal className={styles.modal}>
          <div className={styles.content}>
            <h1 data-testid="edit profile header">
              <b>{formatMessage(messages.modalTitle)}</b>
            </h1>
            <Formik
              initialValues={{
                email: profile.email,
                phone: profile.phone ? profile.phone : '',
                first_name: profile.first_name,
                last_name: profile.last_name ? profile.last_name : '',
              }}
              validate={fieldValidators}
              onSubmit={onSubmit}
              enableReinitialize
              render={({ values, handleChange, errors, setFieldValue }) => {
                return (
                  <Form>
                    <Grid>
                      <Cell small={12}>
                        <FieldInput
                          autoComplete="first_name"
                          autoFocus={true}
                          label={formatMessage(messages.firstNamePlaceholder)}
                          errorText={errors.first_name}
                          name="first_name"
                          value={values.first_name}
                          onChange={handleChange}
                          data-testid="first_name"
                          label-testid="first_name label"
                          error-testid="first_name error"
                        />
                      </Cell>
                      <Cell small={12}>
                        <FieldInput
                          autoComplete="last_name"
                          autoFocus={true}
                          label={formatMessage(messages.lastNamePlaceholder)}
                          errorText={errors.last_name}
                          name="last_name"
                          value={values.last_name}
                          onChange={handleChange}
                          data-testid="last_name"
                          label-testid="last_name label"
                          error-testid="last_name error"
                        />
                      </Cell>
                      <Cell small={12}>
                        <FieldInput
                          autoComplete="email"
                          label={formatMessage(messages.emailPlaceholder)}
                          errorText={errors.email}
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          data-testid="email"
                          disabled
                          label-testid="email label"
                          error-testid="email error"
                        />
                      </Cell>
                      <Cell small={12}>
                        <FieldInput
                          label={formatMessage(messages.phonePlaceholder)}
                          errorText={errors.phone}
                          name="phone"
                          value={values.phone}
                          onChange={handleChange}
                          data-testid="phone"
                          label-testid="phone label"
                          error-testid="phone error"
                        />
                      </Cell>
                      <Cell
                        small={12}
                        flexContainer
                        className={styles['actions-container']}
                      >
                        <SecondaryButton
                          label={formatMessage(messages.cancelBtn)}
                          className="font-18"
                          type="button"
                          onClick={() => setShowEditDialog(false)}
                          data-testid="cancel button"
                        />
                        <PrimaryButton
                          label={formatMessage(messages.saveBtn)}
                          disabled={isDataLoading}
                          className="font-18"
                          type="submit"
                          data-testid="save button"
                        />
                      </Cell>
                    </Grid>
                  </Form>
                );
              }}
            />
          </div>
        </Modal>
      )}
      {showCloseAccountlModal && (
        <Modal className={styles.closeAccountModal}>
          <div className={styles.content}>
            <div
              className={styles.closeButton}
              onClick={() => {
                setShowCloseAccountlModal(false);
              }}
            >
              <img src={require(`../../../img/close.svg`)} alt={`Close`} />
            </div>
            <h1>
              <b>Are you sure you want to close your account?</b>
            </h1>
            <div className={styles.box}>
              <p>This action will redirect you to the login page.</p>
            </div>
            <PrimaryButton
              label="Yes, close my account"
              onClick={() => {
                onCloseAccount();
              }}
            />
            <SecondaryButton
              label="No"
              onClick={() => {
                setShowCloseAccountlModal(false);
              }}
            />
          </div>
        </Modal>
      )}
      <div
        className={showProfilePicDialog ? styles.showDialog : styles.hideDialog}
      >
        <Modal
          className={[
            styles.profileModal,
            showProfilePicDialog ? 'showDialog' : '',
          ].join(' ')}
        >
          <div className={styles.content}>
            <h1>
              <b>{formatMessage(messages.profilePicModalTitle)}</b>
            </h1>
            <p>{formatMessage(messages.profilePicModalFileSize)}</p>
            {error && <p className={styles.error}>{error}</p>}
            <div className={styles.cropper}>
              <ReactCrop
                src={image}
                crop={cropObj}
                ruleOfThirds
                onImageLoaded={onImageLoaded}
                onComplete={onCropComplete}
                onChange={onCropChange}
                minHeight={160}
                minWidth={160}
                circularCrop={true}
              />
            </div>
          </div>
          <Grid className={styles['crop-action-grid']}>
            <Dropzone onDrop={onDrop}>
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <Cell
                    medium={5}
                    small={6}
                    className={styles['actions-container-left']}
                  >
                    <SecondaryButton
                      label={formatMessage(messages.profilePicChooseAnotherBtn)}
                      className="font-16"
                      type="button"
                      onClick={() => setCropObj(cropConfig)}
                    />
                  </Cell>
                </div>
              )}
            </Dropzone>
            <Cell
              medium={7}
              small={6}
              className={styles['actions-container-right']}
            >
              <SecondaryButton
                label={formatMessage(messages.profilePicCancelBtn)}
                className="font-16"
                type="button"
                onClick={() => {
                  setShowProfilePicDialog(false);
                  setCropObj(cropConfig);
                }}
              />
              <PrimaryButton
                label={formatMessage(messages.profilePicSaveBtn)}
                disabled={isDataLoading}
                className="font-18"
                type="button"
                onClick={() => onClickUploadProfilePic()}
              />
            </Cell>
          </Grid>
        </Modal>
      </div>
    </div>
  );
};

export default PatientProfilePage;
